<template>
  <div
    v-if="currentLandscape"
    class="LandscapeView"
    :class="{'is-tree': showTree && isMobile}"
  >
    <lm-a2-h-s-prompt />
    <tree-container />
    <landscape-nav-mobile />
    <div class="LandscapeContainer">
      <lm-backdrop v-show="showTree" :open="showTree" @click="closeTree" />
      <actor-nav v-show="showActorNav" />
      <transition name="fade" mode="out-in" @after-leave="toggleHideTree">
        <router-view />
      </transition>
    </div>
  </div>
  <p
    v-else-if="errorLoading"
    class="Landscape-errorMessage"
    v-html="errorMessage"
  />
</template>

<script>
import LmA2HSPrompt from '@/components/ui/LmA2HSPrompt'
import {mapGetters} from 'vuex'
import {Events} from '@/lib/definitions'
import EventHub from '@/lib/eventHub'
import LmBackdrop from '@/components/common/LmBackdrop'
import LandscapeNavMobile from './nav/LandscapeNavMobile'
import ActorNav from './tree/ActorNav'
import TreeContainer from './tree/TreeContainer'
import {ErrorCodes} from '@/lib/definitions'
import socket from '@/lib/socket'

export default {
  name: 'Landscape',
  components: {
    LmA2HSPrompt,
    LmBackdrop,
    TreeContainer,
    ActorNav,
    LandscapeNavMobile
  },
  data() {
    return {
      errorLoading: false,
      errorMessage: null,
      showTree: false
    }
  },
  computed: {
    ...mapGetters([
      'startPage',
      'currentLandscape',
      'invitedLandscapes',
      'findLandscapeBySlug',
      'isMobile',
      'userId'
    ]),
    showActorNav() {
      return !this.isInsightsSection && !this.isSettingsSection
    },
    isInsightsSection() {
      return this.$route.meta.insightsView
    },
    isSettingsSection() {
      return this.$route.meta.settingsSection
    },
    currentLandscapeSlug() {
      return this.$route.params.landscapeSlug || this.startPage
    }
  },
  watch: {
    $route(to, from) {
      if (to.params.landscapeSlug !== from.params.landscapeSlug) {
        this.loadLandscape() // Landscape changed
      }
      if (to.params.actor !== from.params.actor) {
        this.$store.dispatch('setSelectedActor') // Actor changed
      }
    },
    currentLandscape() {
      if (!this.showActorNav) {
        this.$nextTick(() => {
          this.toggleHideTree()
        })
      }
    }
  },
  created() {
    this.loadLandscape()
  },
  mounted() {
    EventHub.on(Events.OPEN_TREE, this.openTree)
    EventHub.on(Events.TOGGLE_TREE, this.toggleTree)
    EventHub.on(Events.CLOSE_TREE, this.closeTree)
  },
  beforeDestroy() {
    this.$store.dispatch('unloadLandscape')
    EventHub.removeListener(Events.OPEN_TREE, this.openTree)
  },
  methods: {
    openTree() {
      this.showTree = true
      this.$store.dispatch('hideScrollbar')
    },
    closeTree() {
      this.showTree = false
      // Restore scroll when CSS transition finish!!
      setTimeout(() => {
        this.$store.dispatch('showScrollbar')
      }, 600)
    },
    toggleTree() {
      this.showTree ? this.closeTree() : this.openTree()
    },
    loadLandscape() {
      this.errorLoading = false
      this.setTitle() // Set ASAP for animation
      const landscapeId = this.validateSlug()
      if (landscapeId) {
        this.$store
          .dispatch('loadLandscape', landscapeId)
          .then(landscape => {
            socket.create(this.userId, landscape._id, this.$store)
          })
          .catch(errorStatus => {
            this.errorLoading = true
            this.$store.dispatch('unloadLandscape')
            this.$store.dispatch('sectionTitle', `Error loading landscape`)
            const name = this.findLandscapeName()
            if (errorStatus.devCode === ErrorCodes.LANDSCAPE_LOCKED) {
              this.errorMessage = `This landscape is currently locked. For additional information please refer to the account owner of <span class="LandscapeName">${name}.</span>`
            } else {
              console.log('loadLandscape error => ', errorStatus)
              this.errorMessage = `We couldn't load <span class="LandscapeName">${name}</span> (${errorStatus.devCode} - ${errorStatus.devMessage})`
            }
          })
      } else {
        this.$store.dispatch('sectionTitle', 'Landscape not found')
        // TODO more info + links to others landscapes
      }
    },
    validateSlug() {
      let landscape = this.findLandscapeBySlug(this.currentLandscapeSlug)
      if (!landscape) return
      if (landscape.slug === this.$route.params.landscapeSlug) {
        return landscape._id
      } else {
        this.$router.replace({params: {landscapeSlug: landscape.slug}})
      }
    },
    findLandscapeName() {
      let landscape = this.findLandscapeBySlug(this.currentLandscapeSlug)
      if (landscape) return landscape.name
      return null // Invitation case
    },
    setTitle() {
      const name = this.findLandscapeName()
      if (name) {
        this.$store.dispatch('sectionTitle', name)
      }
    },
    toggleHideTree() {
      document
        .querySelector('.LandscapeView')
        .classList.toggle('hide-tree', !this.showActorNav)
    }
  }
}
</script>

<style scoped>
.LandscapeView {
  min-height: theme('minHeight.screen-page');
  padding-bottom: theme('height.nav-mobile'); /* LandscapeNavMobile offset */
  &.is-tree {
    .TreeContainer {
      transform: translateX(0);
    }
    .LandscapeContainer {
      transform: translateX(300px);
    }
  }
}
.TreeContainer {
  position: absolute;
  top: theme('height.nav-mobile');
  bottom: theme('height.nav-mobile');
  transform: translateX(-300px);
}
.TreeContainer,
.LandscapeContainer {
  transition: transform 0.6s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.LandscapeContainer .LmBackdrop {
  z-index: calc(theme('zIndex.navMobile') + 1);
}
@media (--desktop) {
  .LandscapeView {
    display: grid;
    grid-template: auto / theme('width.tree') auto;
    /* Reset */
    padding: 0;
    min-height: auto;
    &.hide-tree {
      .TreeContainer {
        display: none;
      }
      .LandscapeContainer {
        grid-column: 1 / -1;
      }
    }
  }
  .LandscapeContainer {
    overflow: hidden; /* BUGFIX Grid + Ellipsis not working */
  }
  .LandscapeNavMobile,
  .LandscapeContainer .LmBackdrop,
  .LandscapeContainer .ActorNav {
    display: none;
  }
  .TreeContainer {
    grid-column: 1 / span 1;
    grid-row: 1 / -1;
    height: theme('height.screen-page');
    /* Reset */
    position: relative;
    top: 0;
    transform: none;
  }
  .LandscapeView.is-tree .LandscapeContainer {
    transform: none;
  }
}
.Landscape-errorMessage {
  max-width: 500px;
  margin-top: 100px;
  padding: 0 20px;
  .LandscapeName {
    color: theme('colors.highlight');
  }
}
</style>
